import { useState, useEffect } from "react";
import useAPI from "../hooks/useAPI";
import shortid from "shortid";

import { useDispatch } from "react-redux";
import allActions from "../state/items/actions/index";
import { Storage } from "aws-amplify";

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    if (["0", "false", "no", "off", ""].includes(normalized)) return false;
  }
  return Boolean(value);
};

const toOneZeroString = (value) => (toBoolean(value) ? "1" : "0");

export default function useAdminItem(props) {
  const dispatch = useDispatch();
  const localUser = JSON.parse(localStorage.getItem("kdmUser"));
  const superUser = localUser.sub === process.env.REACT_APP_ADMIN_UID;

  const { currentItem, callback, onSave } = props;

  const { data, query: fetchData } = useAPI({
    type: "get",
    url: `items`,
    defer: true,
  });

  const { data: dataMenus } = useAPI({
    type: "get",
    url: `categories`,
  });

  const [title, setTitle] = useState(currentItem?.title || "");
  const [location, setLocation] = useState(currentItem?.location || "");
  const payProviderMetadata = currentItem?.payProviderMetadata || {};
  const [description, setDescription] = useState(
    currentItem?.description || ""
  );
  const [prices, setPrices] = useState(currentItem?.prices || []);
  const [nb, setNb] = useState(currentItem?.nb || 0);
  const available = currentItem?.available || 0;
  const busy = currentItem?.busy || 0;
  const [files, setFiles] = useState(currentItem?.files || []);
  const [tags, setTags] = useState(currentItem?.tags || []);
  const [tagsAsMenu, setTagsAsMenu] = useState(currentItem?.tags || []);
  const [dimensions, setDimensions] = useState(currentItem?.dimensions || []);
  const [published, setPublished] = useState(
    toOneZeroString(currentItem?.published)
  );
  const [isRecurrent, setSubscription] = useState(
    toOneZeroString(currentItem?.isRecurrent)
  );
  const [hasMultiplePrices, setHasMultiplePrices] = useState(
    toBoolean(currentItem?.hasMultiplePrices)
  );
  const [isFreeTicket, setIsFreeTicket] = useState(
    toBoolean(currentItem?.isFreeTicket)
  );
  const [isEvent, setIsEvent] = useState(toOneZeroString(currentItem?.isEvent));
  const [requiredFormular, setRequireFormular] = useState(
    toOneZeroString(currentItem?.requiredFormular)
  );
  const [eventPlanner, setEventPlanner] = useState(
    currentItem?.eventPlanner || {
      dateEvent: { start: null, end: null },
      dateSale: { start: null, end: null },
    }
  );

  const platforms = currentItem?.platforms || [];
  const [submitting, setSubmitting] = useState(false);
  const [dialogPricing, setDialogPricing] = useState(false);

  const { query: postItem } = useAPI({
    defer: true,
    type: "post",
    url: "item",
    body: {
      uid: btoa(localUser.sub),
      title,
      description,
      location,
      tags,
      prices,
      filesUploaded: files,
      dimensions,
      published,
      isRecurrent,
      hasMultiplePrices,
      isFreeTicket,
      nb,
      eventPlanner,
      isEvent,
      requiredFormular,
    },
  });

  const { query: updateItem } = useAPI({
    defer: true,
    type: "post",
    url: `item`,
    body: {
      id: currentItem?.id || 0,
      value: "put",
      title,
      description,
      location,
      tags: tagsAsMenu,
      prices,
      filesUploaded: files,
      dimensions,
      published,
      isRecurrent,
      hasMultiplePrices,
      isFreeTicket,
      nb,
      eventPlanner,
      isEvent,
      requiredFormular,
    },
  });

  const handleFileChange = async (e) => {
    try {
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        const filename = `kadomi/${shortid.generate()}-${file.name}`;

        await Storage.put(filename, file, {
          contentType: file.type,
          progressCallback(progress) {
            const progressPercent = (progress.loaded / progress.total) * 100;
            console.log(`Uploaded: ${Math.round(progressPercent)}%`);
          },
        });

        setFiles((prev) => [...prev, filename]);
      }
    } catch (error) {
      console.error("Authentication or upload error:", error);
    }
  };

  const handlePostItem = async () => {
    const item = {
      title,
      description,
      location,
      tags: tagsAsMenu,
      prices,
      filesUploaded: files,
    };

    setSubmitting(true);
    try {
      if (currentItem?.id) {
        await updateItem(item);
      } else {
        await postItem(item);
      }
      await fetchData();
      setSubmitting(false);
      onSave({ success: true, message: "Article sauvegardé avec succès" });
      callback?.();
    } catch (err) {
      console.log(err);
      onSave({
        success: false,
        message: "Erreur à la sauvegarde de l'article",
      });
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (data?.items) {
      dispatch(allActions.itemActions.fetchAll(data.items));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (currentItem) {
      setTitle(currentItem.title || "");
      setLocation(currentItem.location || "");
      setDescription(currentItem.description || "");
      setPrices(currentItem.prices || []);
      setNb(currentItem.nb || 0);
      setFiles(currentItem.files || []);
      setTags(currentItem.tags || []);
      setTagsAsMenu(currentItem.tags || []);
      setDimensions(currentItem.dimensions || []);
      setPublished(toOneZeroString(currentItem.published));
      setSubscription(toOneZeroString(currentItem.isRecurrent));
      setHasMultiplePrices(toBoolean(currentItem.hasMultiplePrices));
      setIsFreeTicket(toBoolean(currentItem.isFreeTicket));
      setIsEvent(toOneZeroString(currentItem.isEvent));
      setRequireFormular(toOneZeroString(currentItem.requiredFormular));
      setEventPlanner(
        currentItem.eventPlanner || {
          dateEvent: { start: null, end: null },
          dateSale: { start: null, end: null },
        }
      );
    }
  }, [currentItem]);

  return {
    payProviderMetadata,
    title,
    description,
    location,
    prices,
    nb,
    available,
    busy,
    tags,
    tagsAsMenu,
    files,
    dimensions,
    platforms,
    published,
    isRecurrent,
    hasMultiplePrices,
    isFreeTicket,
    submitting,
    currentItem,
    dialogPricing,
    superUser,
    eventPlanner,
    isEvent,
    requiredFormular,
    dataMenus: dataMenus?.menus || [],
    handlePostItem,
    onTitleChange: (e) => setTitle(e.target.value),
    onDescriptionChange: (e) => setDescription(e.target.value),
    onLocationChange: (e) => setLocation(e.target.value),
    onTagsChange: setTags,
    onTagsAsMenuChange: (value) =>
      setTagsAsMenu((prev) =>
        prev.includes(value)
          ? prev.filter((t) => t !== value)
          : [...prev, value]
      ),
    onNbItemChange: (e) => {
      const val = parseInt(e.target.value || 0, 10);
      if (val <= 1000) setNb(val);
    },
    onFileChange: handleFileChange,
    onChangeDimension: (value, type, multipleChoice) => {
      console.log("onChangeDimension:", type);
      const idx = dimensions.findIndex((d) => d.type === type);
      if (idx !== -1) {
        const newDimensions = [...dimensions];
        newDimensions[idx].items = value;
        // newDimensions[idx].multipleChoice = multipleChoice;
        setDimensions(newDimensions);
      }
    },
    onDeleteDimension: (type) =>
      setDimensions(dimensions.filter((d) => d.type !== type)),
    onUpdateDimension: (
      originalType,
      dimension,
      type,
      multipleChoice,
      maxChoices,
      paidSupplement = false,
      amountSupplement = 0,
      optionalDimension
    ) => {
      // if maxChoices is not a number, then set it to 1
      if (isNaN(maxChoices) || maxChoices < 1) {
        maxChoices = 1;
      }
      const updatedDimensions = dimensions.map((d) =>
        d.type === originalType
          ? {
              ...d,
              title: dimension,
              type,
              multipleChoice,
              maxChoices: multipleChoice ? maxChoices : 1,
              paidSupplement,
              amountSupplement,
              optionalDimension,
            }
          : d
      );
      setDimensions(updatedDimensions);
      return true;
    },
    onAddDimension: (
      dimension,
      type,
      multipleChoice,
      maxChoices,
      paidSupplement = false,
      amountSupplement = 0,
      optionalDimension,
      items = []
    ) => {
      const exists = dimensions.some((d) => d.type === type);
      if (!exists) {
        // if maxChoices is not a number, then set it to 1
        if (isNaN(maxChoices) || maxChoices < 1) {
          maxChoices = 1;
        }
        setDimensions([
          ...dimensions,
          {
            title: dimension,
            type,
            items: items,
            multipleChoice,
            maxChoices: multipleChoice ? maxChoices : 1,
            paidSupplement,
            amountSupplement,
            optionalDimension,
          },
        ]);
        return true;
      }
      return false;
    },
    onSubscriptionChange: (e) => {
      const value = e.target.value;
      console.log("Subscription value:", value);
      if (value === "1") {
        setSubscription("1");
      } else {
        setSubscription("0");
      }
    },
    onMultiplePricesChange: (e) => {
      // toggle hasMultiplePrices boolean
      const checked = e.target.checked;
      console.log("onMultiplePricesChange:", checked);
      setHasMultiplePrices(checked);
    },
    onFreeTicketChange: (e) => {
      const checked = e.target.checked;
      console.log("onFreeTicketChange:", checked);
      setIsFreeTicket(checked);
    },
    onStatusPublishedChange: (e) => setPublished(e.target.value),
    openDialogPricing: () => setDialogPricing(true),
    onDialogClosePricing: () => setDialogPricing(false),
    onSavePrice: (price, currency, priceLabel) => {
      console.log("onSavePrice:", price, currency, priceLabel);
      if (!currency) return;

      // si hasMultiplePrices est a FALSE, on exclue la currency du tableau
      // et on ajoute le nouveau prix
      // sinon on ajoute simplement le nouveau prix

      const newPrices = !hasMultiplePrices
        ? [
            ...prices.filter((p) => p.currency !== currency),
            { price, currency, priceLabel },
          ]
        : [...prices, { price, currency, priceLabel }];

      setPrices(newPrices);
      setDialogPricing(false);
    },
    onDeleteSavedPrice: (currency) =>
      setPrices(prices.filter((p) => p.currency !== currency)),
    onEventPlanner: (date, id, moment) => {
      const updated = { ...eventPlanner };
      // Si c'est déjà une string (champ texte), on l'utilise directement
      // Sinon si c'est un objet Date, on appelle toJSON()
      updated[id][moment] = typeof date === "string" ? date : date.toJSON();
      setEventPlanner(updated);
    },
    onSetEvent: (e) => setIsEvent(e.target.checked ? "1" : "0"),
    onSetFormular: (e) => setRequireFormular(e.target.checked ? "1" : "0"),
    onSaveMenuForItemRefresh: () => console.log("handleSaveMenuForItemRefresh"),
    onHandleDeleteFile: (f, key) => setFiles(files.filter((_, k) => k !== key)),
  };
}
